import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_MultipleColumns() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📑 Advanced CSS: Multiple Columns</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Multi-Column Layout allows you to split content into multiple
        vertical columns, similar to newspapers. Perfect for text-heavy layouts,
        responsive designs, and magazine-style pages.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#78350f" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>column-count</code> to set the number of columns.
          </li>
          <li>
            Use <code>column-gap</code> to control spacing between columns.
          </li>
          <li>
            Use <code>column-rule</code> to add vertical separators.
          </li>
          <li>
            Use <code>break-inside: avoid;</code> to prevent unwanted breaks in
            elements.
          </li>
          <li>
            Combine with <code>backgrounds, gradients, hover</code> for advanced
            styling.
          </li>
        </ul>
      </div>

      {/* Demo Section */}
      <div className="tutorial-section">
        <h2 className="section-title">📝 Multi-Column Examples</h2>

        {/* Example 1: Simple 3 columns */}
        <h3>1. Three Columns Layout</h3>
        <CodeCopyBlock
          code={`.multi-column { column-count: 3; column-gap: 20px; }`}
        />
        <div
          className="multi-column"
          style={{
            columnCount: 3,
            columnGap: "20px",
            background: "#fefce8",
            padding: "15px",
            borderRadius: "10px",
            marginBottom: "1rem",
          }}
        >
          <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
          <p>
            Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.
          </p>
          <p>Sed nisi. Nulla quis sem at nibh elementum imperdiet.</p>
          <p>
            Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue
            semper porta.
          </p>
          <p>Mauris massa. Vestibulum lacinia arcu eget nulla.</p>
        </div>

        {/* Example 2: Columns with rule */}
        <h3>2. Columns with Vertical Rule</h3>
        <CodeCopyBlock
          code={`.multi-column-rule { column-count: 2; column-gap: 25px; column-rule: 2px solid #f97316; }`}
        />
        <div
          style={{
            columnCount: 2,
            columnGap: "25px",
            columnRule: "2px solid #f97316",
            background: "#fff7ed",
            padding: "15px",
            borderRadius: "10px",
            marginBottom: "1rem",
          }}
        >
          <p>Curabitur sodales ligula in libero.</p>
          <p>Sed dignissim lacinia nunc.</p>
          <p>Curabitur tortor. Pellentesque nibh.</p>
          <p>Aenean quam. In scelerisque sem at dolor.</p>
        </div>

        {/* Example 3: Responsive columns */}
        <h3>3. Responsive Columns</h3>
        <CodeCopyBlock
          code={`.responsive-columns { column-count: 1; } @media (min-width: 768px) { .responsive-columns { column-count: 2; } } @media (min-width: 1024px) { .responsive-columns { column-count: 3; } }`}
        />
        <div
          style={{
            columnCount: 1,
            columnGap: "20px",
            background: "#f0fdf4",
            padding: "15px",
            borderRadius: "10px",
            marginBottom: "1rem",
          }}
        >
          <p>Responsive design adapts to screen size.</p>
          <p>Columns increase automatically on larger screens.</p>
          <p>Perfect for text-heavy layouts and blogs.</p>
          <p>Try resizing your browser to see the effect.</p>
        </div>

        {/* Example 4: Avoid breaks */}
        <h3>4. Avoid Breaking Cards</h3>
        <CodeCopyBlock
          code={`.card { break-inside: avoid; padding: 10px; background: #dbeafe; margin-bottom: 10px; border-radius: 8px; }`}
        />
        <div
          style={{ columnCount: 3, columnGap: "15px", marginBottom: "1rem" }}
        >
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              style={{
                breakInside: "avoid",
                padding: "10px",
                background: "#dbeafe",
                marginBottom: "10px",
                borderRadius: "8px",
              }}
            >
              <p>Card {i} content goes here.</p>
            </div>
          ))}
        </div>

        {/* NEW STYLES */}
        <h2>✨ Advanced Multi-Column Styles</h2>

        {/* Example 5: Magazine style alternating */}
        <h3>5. Magazine Style Alternating Background</h3>
        <CodeCopyBlock
          code={`.magazine-columns p:nth-child(odd) { background: #fef3c7; padding: 5px; border-radius: 5px; } .magazine-columns p:nth-child(even) { background: #fde68a; padding: 5px; border-radius: 5px; }`}
        />
        <div
          style={{ columnCount: 2, columnGap: "20px", marginBottom: "1rem" }}
        >
          {[...Array(6)].map((_, i) => (
            <p
              key={i}
              style={{
                background: i % 2 === 0 ? "#fef3c7" : "#fde68a",
                padding: "5px",
                borderRadius: "5px",
              }}
            >
              Paragraph {i + 1} for magazine style
            </p>
          ))}
        </div>

        {/* Example 6: Glassmorphism Cards */}
        <h3>6. Glassmorphism Cards</h3>
        <CodeCopyBlock
          code={`.glass-card { background: rgba(255,255,255,0.2); backdrop-filter: blur(8px); padding: 15px; border-radius: 10px; margin-bottom: 10px; }`}
        />
        <div
          style={{ columnCount: 3, columnGap: "15px", marginBottom: "1rem" }}
        >
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              style={{
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(8px)",
                padding: "15px",
                borderRadius: "10px",
                marginBottom: "10px",
              }}
            >
              <p>Glass Card {i + 1}</p>
            </div>
          ))}
        </div>

        {/* Example 7: Gradient Column Backgrounds */}
        <h3>7. Gradient Column Backgrounds</h3>
        <CodeCopyBlock
          code={`.gradient-column { background: linear-gradient(45deg,#f43f5e,#f97316); padding: 10px; border-radius: 8px; color: white; margin-bottom: 10px; }`}
        />
        <div
          style={{ columnCount: 2, columnGap: "20px", marginBottom: "1rem" }}
        >
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              style={{
                background: "linear-gradient(45deg,#f43f5e,#f97316)",
                padding: "10px",
                borderRadius: "8px",
                color: "white",
                marginBottom: "10px",
              }}
            >
              Gradient Item {i + 1}
            </div>
          ))}
        </div>

        {/* Example 8: Hover Effect Cards */}
        <h3>8. Hover Effect Cards</h3>
        <CodeCopyBlock
          code={`.hover-card { padding: 10px; background: #dbeafe; border-radius: 8px; transition: transform 0.3s ease; } .hover-card:hover { transform: scale(1.05); }`}
        />
        <div
          style={{ columnCount: 3, columnGap: "15px", marginBottom: "1rem" }}
        >
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              style={{
                padding: "10px",
                background: "#dbeafe",
                borderRadius: "8px",
                transition: "transform 0.3s ease",
                marginBottom: "10px",
                cursor: "pointer",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.05)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
            >
              Hover Card {i + 1}
            </div>
          ))}
        </div>

        {/* Example 9: Image + Text Columns */}
        <h3>9. Image + Text Cards</h3>
        <CodeCopyBlock
          code={`.image-card { break-inside: avoid; margin-bottom: 10px; border-radius: 8px; overflow: hidden; } .image-card img { width: 100%; display: block; } .image-card p { padding: 8px; background: #fefce8; margin: 0; }`}
        />
        <div
          style={{ columnCount: 2, columnGap: "20px", marginBottom: "1rem" }}
        >
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              style={{
                breakInside: "avoid",
                marginBottom: "10px",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <img
                src={`https://picsum.photos/300/200?random=${i + 1}`}
                alt={`Random ${i + 1}`}
              />
              <p>Image Card {i + 1}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#78350f" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <strong>column-span</strong> for headlines across
            columns.
          </li>
          <li>
            Combine <strong>CSS Grid + Columns</strong> for complex layouts.
          </li>
          <li>
            Animate column content using <strong>CSS transitions</strong>.
          </li>
          <li>
            Use <strong>gradient + hover + glassmorphism</strong> together for
            creative designs.
          </li>
        </ul>
      </div>
    </div>
  );
}
